import type {
  PlanExceptionKind, PlanLifecycleState, PlanRecord,
} from '../core/types.js';
import { localDate, nowIso, tzOffsetMinutes } from '../core/time.js';
import { logEvent, plans } from '../storage/db.js';

export function effectiveLifecycle(plan: PlanRecord, nowMs: number = Date.now()): PlanLifecycleState {
  if (plan.lifecycleState === 'paused' && plan.pausedUntil) {
    if (new Date(plan.pausedUntil).getTime() <= nowMs) return 'active';
  }
  if (plan.graduatedAt) return plan.lifecycleState ?? 'graduated';
  return plan.lifecycleState ?? 'active';
}

export function isPaused(plan: PlanRecord, nowMs: number = Date.now()): boolean {
  return effectiveLifecycle(plan, nowMs) === 'paused';
}

async function transition(
  plan: PlanRecord,
  to: PlanLifecycleState,
  type: 'plan_paused' | 'plan_resumed' | 'plan_graduated' | 'plan_maintenance_started' | 'plan_archived',
  patch: Partial<PlanRecord>,
  reason?: string,
): Promise<PlanRecord> {
  const from = plan.lifecycleState ?? 'active';
  const updated: PlanRecord = {
    ...plan,
    ...patch,
    lifecycleState: to,
    updatedAt: nowIso(),
  };
  await plans.put(updated);
  const occurredAt = nowIso();
  await logEvent({
    type,
    habitId: plan.habitId,
    planId: plan.id,
    occurredAt,
    localDate: localDate(),
    timezoneOffsetMinutes: tzOffsetMinutes(),
    payload: {
      planId: plan.id, from, to,
      ...(reason ? { reason } : {}),
    },
  });
  return updated;
}

export async function pausePlan(plan: PlanRecord, until?: string, reason?: string): Promise<PlanRecord> {
  return transition(plan, 'paused', 'plan_paused', { pausedUntil: until }, reason);
}

export async function resumePlan(plan: PlanRecord): Promise<PlanRecord> {
  return transition(plan, 'active', 'plan_resumed', { pausedUntil: undefined });
}

export async function graduatePlan(plan: PlanRecord): Promise<PlanRecord> {
  return transition(plan, 'graduated', 'plan_graduated', { graduatedAt: nowIso() });
}

export async function moveToMaintenance(plan: PlanRecord): Promise<PlanRecord> {
  return transition(plan, 'maintenance', 'plan_maintenance_started', {});
}

export interface LogExceptionInput {
  plan: PlanRecord;
  kind: PlanExceptionKind;
  startedAt?: string;
  endedAt?: string;
  note?: string;
}

export async function logPlanException(input: LogExceptionInput): Promise<void> {
  const occurredAt = input.startedAt ?? nowIso();
  await logEvent({
    type: 'plan_exception_logged',
    habitId: input.plan.habitId,
    planId: input.plan.id,
    occurredAt,
    localDate: localDate(new Date(occurredAt)),
    timezoneOffsetMinutes: tzOffsetMinutes(new Date(occurredAt)),
    payload: {
      planId: input.plan.id,
      kind: input.kind,
      startedAt: occurredAt,
      ...(input.endedAt ? { endedAt: input.endedAt } : {}),
      ...(input.note ? { note: input.note } : {}),
    },
  });
}

export function lifecycleLabel(state: PlanLifecycleState): string {
  switch (state) {
    case 'draft': return 'Draft';
    case 'calibration': return 'Calibration';
    case 'active': return 'Active';
    case 'paused': return 'Paused';
    case 'maintenance': return 'Maintenance';
    case 'graduated': return 'Graduated';
    case 'archived': return 'Archived';
  }
}

export const EXCEPTION_OPTIONS: Array<{ value: PlanExceptionKind; label: string }> = [
  { value: 'travel', label: 'Travel' },
  { value: 'sick', label: 'Sick day' },
  { value: 'social_event', label: 'Social event' },
  { value: 'high_stress', label: 'High-stress day' },
  { value: 'celebration', label: 'Planned celebration' },
  { value: 'other', label: 'Other' },
];
